import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const UserDetailsModal = ({ isOpen, onClose, user }) => {
  if (!isOpen || !user) return null;

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      accountant: 'bg-blue-100 text-blue-800',
      manager: 'bg-green-100 text-green-800',
      user: 'bg-gray-100 text-gray-800'
    };
    return colors?.[role] || colors?.user;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">User Details</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            aria-label="Close modal"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            {/* User Profile */}
            <div className="flex items-center gap-4 pb-6 border-b border-border">
              <div className="flex-shrink-0 h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="User" size={32} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-lg text-foreground">{user?.fullName}</h3>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <div className="mt-2">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user?.role)}`}>
                    {user?.role}
                  </span>
                </div>
              </div>
            </div>

            {/* Account Information */}
            <div>
              <h4 className="font-heading font-semibold text-base text-foreground mb-3">Account Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Company</p>
                  <p className="text-sm text-foreground">{user?.company || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">User ID</p>
                  <p className="text-sm text-foreground font-mono">{user?.id?.substring(0, 8)}...</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Joined Date</p>
                  <p className="text-sm text-foreground">{formatDate(user?.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Last Updated</p>
                  <p className="text-sm text-foreground">{formatDate(user?.updatedAt)}</p>
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div>
              <h4 className="font-heading font-semibold text-base text-foreground mb-3">Module Permissions</h4>
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                {user?.permissions ? (
                  Object.entries(user?.permissions)?.map(([module, perms]) => (
                    <div key={module} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon name="Shield" size={16} className="text-primary" />
                        <span className="text-sm font-medium text-foreground capitalize">{module}</span>
                      </div>
                      <div className="flex gap-2">
                        {Object.entries(perms)?.map(([action, allowed]) => (
                          allowed && (
                            <span key={action} className="px-2 py-1 text-xs bg-success/10 text-success rounded capitalize">
                              {action}
                            </span>
                          )
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No custom permissions set. Using role defaults.</p>
                )}
              </div>
            </div>

            {/* Role Description */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Role: {user?.role}</h4>
              <p className="text-sm text-blue-800">
                {user?.role === 'admin' && 'Full system access with ability to manage users, configure settings, and access all modules.'}
                {user?.role === 'accountant' && 'Can create and edit transactions, generate reports, and reconcile accounts.'}
                {user?.role === 'manager' && 'Can view and approve transactions, access reports, and manage team activities.'}
                {user?.role === 'user' && 'Basic access to view data and submit expenses for approval.'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6 border-t border-border">
          <Button fullWidth onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;